import {BaseService} from './base.service';
import {IBMParameter, IBMDetailViewModel, BMEditedViewModel} from '../models/bm';
import {Uris} from '../common/constants';
import {IBaseListingModel} from '../models/base-listing.model';
import * as JSONPatch from 'fast-json-patch';
import {IBMMeetingParameter} from "../models/bm/bm-meeting.parameter";
import {IApartment} from "../models/apartment";
import {BMMeetingEditedViewModel} from "../models/bm/bm-meeting-edited.viewmodel";
import {BMParticipantEditedViewModel} from "../models/bm/bm-participant-edited.viewmodel";
import {BMIBEditedViewModel} from "../models/bm/bm-ib-edited.viewmodel";
import {BMContractorEditedViewModel} from "../models/bm/bm-contractor-edited.viewmodel";
import {BMPlanEditedViewModel} from "../models/bm/bm-plan-edited.viewmodel";
import {BMCommentEditedViewModel} from "../models/bm/bm-comment-edited.viewmodel";
import {BMMarkerEditedViewModel} from "../models/bm/bm-marker-edited.viewmodel";
import {IDocument} from "../models/documents/document";

export interface IBMService {
  getBMs(param: IBMParameter, allowPagination?: boolean): Promise<IBaseListingModel>;
  getBMById(id: string): Promise<IBMDetailViewModel>;
  getBMMeetings(payload: { bmid: string, mid: string }, param: IBMMeetingParameter, allowPagination?: boolean): Promise<IBaseListingModel>;
  getMeetingById(bmId: string, id: string): Promise<any>;
  getMeetingPlans(bmId: string, idm: string): Promise<any>;
  getPlanMarkers(bmId: string, idm: string, idp: string): Promise<any>;
  getMeetingIBComments(bmId: string, idm: string, idib: string): Promise<any>;
  postBM(payload: BMEditedViewModel): Promise<any>;
  patchBM(bmid: string, payload: BMEditedViewModel): Promise<any>;
  postBMMarker(bmid: string, idm: string, idp: string, payload: BMMarkerEditedViewModel): Promise<any>;
  patchBMMarker(bmid: string, idm: string, idp: string, idmk: string, payload: BMMarkerEditedViewModel): Promise<any>;
  postBMComment (bmid: string, idm: string, idi: string, payload: BMCommentEditedViewModel): Promise<any>;
  patchBMComment (bmid: string, idm: string, idi: string, idc: string, payload: BMCommentEditedViewModel): Promise<any>;
  postBMPlan (bmid: string, idm: string, payload: BMPlanEditedViewModel): Promise<any>;
  patchBMPlan (bmid: string, idm: string, idp: string, payload: BMPlanEditedViewModel): Promise<any>;
  deleteBMPlan (bmid: string, idm: string, idp: string, force: boolean): Promise<any>;
  postBMContractor (bmid: string, idm: string, idi: string, payload: BMContractorEditedViewModel): Promise<any>;
  patchBMContractor (bmid: string, idm: string, idi: string, idc: string, payload: BMContractorEditedViewModel): Promise<any>;
  postBMIB (bmid: string, idm: string, payload: BMIBEditedViewModel): Promise<any>;
  patchBMIB (bmid: string, idm: string, idi: string, payload: BMIBEditedViewModel): Promise<any>;
  postBMParticipant(bmid: string, idm: string, payload: BMParticipantEditedViewModel): Promise<any>;
  patchBMParticipant(bmid: string, idm: string, idp: string, payload: BMParticipantEditedViewModel): Promise<any>;
  postMeeting(bmid: string, payload: BMMeetingEditedViewModel): Promise<any>;
  patchMeeting(bmid: string, idm: string, payload: BMMeetingEditedViewModel): Promise<any>;
  postMedia (payload: any) : Promise<any>;
  getDocumentAttachments(params: any) : Promise<any>;
  saveDocument (document: IDocument): Promise<any>;
  //getBMTypes(): Promise<any>;
}

export class BMService extends BaseService implements IBMService {
  bmParameter: IBMParameter;
  bmMeetingParameter: IBMMeetingParameter;

  async getBMById (id: string): Promise<any> {
    return super.get(Uris.BMById.replace(':id', id))
  }

  async getMeetingById(bmId: string, idm: string): Promise<any> {
    return super.get(Uris.BMMeetingById
      .replace(':id', bmId)
      .replace(':idm', idm))
  }

  async getBMs(param: IBMParameter, allowPagination?: boolean): Promise<any> {

    let cookedParameter = JSON.parse(JSON.stringify(param))
    if (!this.bmParameter) {
      this.bmParameter = cookedParameter
    } else {
      const changes = JSONPatch.compare(this.bmParameter, cookedParameter)
      const orderChanges = changes.find(x => x.path.indexOf('order') > -1)
      if (orderChanges) {
        const paths: string[] = orderChanges.path.split('/')
        const index: number = Number(paths[paths.length - 1])
        this.bmParameter = cookedParameter
        cookedParameter = {
          ...cookedParameter,
          orderKeys: [cookedParameter.orderKeys[index]],
          orderValues: [cookedParameter.orderValues[index]]
        }
      }
    }

    return super.get(Uris.BM, cookedParameter)
      .then((rawTickets: IBaseListingModel) => {
        if (allowPagination) {
          return Promise.resolve(rawTickets)
        }
        return Promise.resolve(rawTickets.data)
      })
      .catch(error => Promise.reject(error))
  }

  async getBMMeetings (payload: { bmid: string }, param: IBMMeetingParameter, allowPagination?: boolean): Promise<any> {

    let cookedParameter = JSON.parse(JSON.stringify(param))
    if (!this.bmMeetingParameter) {
      this.bmMeetingParameter = cookedParameter
    } else {
      const changes = JSONPatch.compare(this.bmMeetingParameter, cookedParameter)
      const orderChanges = changes.find(x => x.path.indexOf('order') > -1)
      if (orderChanges) {
        const paths: string[] = orderChanges.path.split('/')
        const index: number = Number(paths[paths.length - 1])
        this.bmMeetingParameter = cookedParameter
        cookedParameter = {
          ...cookedParameter,
          orderKeys: [cookedParameter.orderKeys[index]],
          orderValues: [cookedParameter.orderValues[index]]
        }
      }
    }

    return super.get(Uris.BMMeetings
        .replace(':id', payload.bmid)
      , cookedParameter)
      .then((rawTickets: IBaseListingModel) => {
        if (allowPagination) {
          return Promise.resolve(rawTickets)
        }
        return Promise.resolve(rawTickets.data)
      })
      .catch(error => Promise.reject(error))
  }

  async getMeetingPlans(bmId: string, idm: string): Promise<any> {
    return super.get(Uris.BMMeetingPlans
      .replace(':id', bmId)
      .replace(':idm', idm))
  }

  async getPlanMarkers (bmId: string, idm: string, idp: string): Promise<any> {
    return super.get(Uris.BMMarker
      .replace(':id', bmId)
      .replace(':idm', idm)
      .replace(':idp', idp))
  }

  async getMeetingIBComments(bmId: string, idm: string, idib: string): Promise<any> {
    return super.get(Uris.BMMeetingIBComments
      .replace(':id', bmId)
      .replace(':idm', idm)
      .replace(':idib', idib))
  }

  postBM (payload: BMEditedViewModel) : Promise<any> {
    return super.post(Uris.BM, payload)
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  patchBM (bmid: string, payload: BMEditedViewModel) : Promise<any> {
    return super.patch(Uris.BMById.replace(':id', bmid), payload)
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  postBMMarker (bmid: string, idm: string, idp: string, payload: BMMarkerEditedViewModel): Promise<any> {
    return super.post(Uris.BMMarker
      .replace(':id', bmid)
      .replace(':idm', idm)
      .replace(':idp', idp), payload)
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  patchBMMarker (bmid: string, idm: string, idp: string, idmk: string, payload: BMMarkerEditedViewModel): Promise<any> {
    return super.patch(Uris.BMMarkerById
        .replace(':id', bmid)
        .replace(':idm', idm)
        .replace(':idp', idp)
        .replace(':idmk', idmk)
      , payload)
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  deleteBMMarker (bmid: string, idm: string, idp: string, idmk: string): Promise<any> {
    return super.delete(Uris.BMMarkerById
        .replace(':id', bmid)
        .replace(':idm', idm)
        .replace(':idp', idp)
        .replace(':idmk', idmk))
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  postBMParticipant (bmid: string, idm: string, payload: BMParticipantEditedViewModel) : Promise<any> {
    return super.post(Uris.BMParticipant
        .replace(':id', bmid)
        .replace(':idm', idm)
      , payload)
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  patchBMParticipant (bmid: string, idm: string, idp: string, payload: BMParticipantEditedViewModel) : Promise<any> {
    return super.patch(Uris.BMParticipantById
      .replace(':id', bmid)
      .replace(':idm', idm)
      .replace(':idp', idp)
      , payload)
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  deleteBMParticipant (bmid: string, idm: string, idp: string) : Promise<any> {
    return super.delete(Uris.BMParticipantById
      .replace(':id', bmid)
      .replace(':idm', idm)
      .replace(':idp', idp)
    )
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  postBMIB (bmid: string, idm: string, payload: BMIBEditedViewModel) : Promise<any> {
    return super.post(Uris.BMIB
        .replace(':id', bmid)
        .replace(':idm', idm)
      , payload)
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  patchBMIB (bmid: string, idm: string, idi: string, payload: BMIBEditedViewModel) : Promise<any> {
    return super.patch(Uris.BMIBById
      .replace(':id', bmid)
      .replace(':idm', idm)
      .replace(':idi', idi)
      , payload)
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  deleteBMIB (bmid: string, idm: string, idi: string) : Promise<any> {
    return super.delete(Uris.BMIBById
      .replace(':id', bmid)
      .replace(':idm', idm)
      .replace(':idi', idi))
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  postBMPlan (bmid: string, idm: string, payload: BMPlanEditedViewModel) : Promise<any> {
    return super.post(Uris.BMPlan
        .replace(':id', bmid)
        .replace(':idm', idm)
      , payload)
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  patchBMPlan (bmid: string, idm: string, idp: string, payload: BMPlanEditedViewModel) : Promise<any> {
    return super.patch(Uris.BMPlanById
      .replace(':id', bmid)
      .replace(':idm', idm)
      .replace(':idp', idp)
      , payload)
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  deleteBMPlan (bmid: string, idm: string, idp: string, force?: boolean) : Promise<any> {
    let param = ''
    if (force)
      param = '?force=true'
    return super.delete(Uris.BMPlanById
      .replace(':id', bmid)
      .replace(':idm', idm)
      .replace(':idp', idp) + param)
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  postBMContractor (bmid: string, idm: string, idi: string, payload: BMContractorEditedViewModel) : Promise<any> {
    return super.post(Uris.BMContractor
        .replace(':id', bmid)
        .replace(':idm', idm)
        .replace(':idi', idi)
      , payload)
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  patchBMContractor (bmid: string, idm: string, idi: string, idc: string, payload: BMContractorEditedViewModel) : Promise<any> {
    return super.patch(Uris.BMContractorById
        .replace(':id', bmid)
        .replace(':idm', idm)
        .replace(':idi', idi)
        .replace(':idc', idc)
      , payload)
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  deleteBMContractor (bmid: string, idm: string, idi: string, idc: string) : Promise<any> {
    return super.delete(Uris.BMContractorById
        .replace(':id', bmid)
        .replace(':idm', idm)
        .replace(':idi', idi)
        .replace(':idc', idc)
      )
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  deleteBMCommentMedia (bmid: string, idm: string, idi: string, idc: string, idf: string) : Promise<any> {
    return super.delete(Uris.BMCommentMediaById
        .replace(':id', bmid)
        .replace(':idm', idm)
        .replace(':idi', idi)
        .replace(':idc', idc)
        .replace(':idf', idf)
      )
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  postBMComment (bmid: string, idm: string, idi: string, payload: BMCommentEditedViewModel) : Promise<any> {
    return super.post(Uris.BMComment
        .replace(':id', bmid)
        .replace(':idm', idm)
        .replace(':idi', idi)
      , payload)
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  postBMCommentMedia (bmid: string, idm: string, idi: string, idc: string, payload: any) : Promise<any> {
    return super.post(Uris.BMCommentMedia
        .replace(':id', bmid)
        .replace(':idm', idm)
        .replace(':idi', idi)
        .replace(':idc', idc)
      , payload)
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  patchBMComment (bmid: string, idm: string, idi: string, idc: string, payload: BMCommentEditedViewModel) : Promise<any> {
    return super.patch(Uris.BMCommentById
        .replace(':id', bmid)
        .replace(':idm', idm)
        .replace(':idi', idi)
        .replace(':idc', idc)
      , payload)
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  postMeeting (bmid: string, payload: BMMeetingEditedViewModel) : Promise<any> {
    return super.post(Uris.BMMeetings.replace(':id', bmid), payload)
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  patchMeeting (bmid: string, idm: string, payload: BMMeetingEditedViewModel) : Promise<any> {
    return super.patch(Uris.BMMeetingById
        .replace(':id', bmid)
        .replace(':idm', idm)
      , payload)
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }

  postMedia (payload: any) : Promise<any> {
    return super.post(Uris.AddMedia, payload)
      .then(response => Promise.resolve(response))
      .catch(err => Promise.reject(err))
  }
  saveDocument (document: IDocument): Promise<any> {
    return this.cookDocument(document)
      .then(cookedDocument => {
        return super.post(Uris.DocumentManage,{ ...cookedDocument })
          .then(response => Promise.resolve(response))
          .catch(err => Promise.reject(err))
      })
  }

  cookDocument (document:IDocument) {
    const { attachments, blocks, apartments, entities, share } = document
    let cookedDocument:any = {
      sharing_type: share,
      blocks,
      apartments,
      entities,
      files: attachments.map((attachment:any) => ({
        fk_attachment_type: parseInt(document.type.toString()),
        ...attachment
      }))
    }
    return Promise.resolve(cookedDocument)
  }
  getDocumentAttachments(params: any) : Promise<any> {
    return super.get(Uris.DocumentTypes,params)
      .then((response: any)=> Promise.resolve(response))
      .catch(err=> err)
  }
}



// WEBPACK FOOTER //
// ./src/services/bm.service.ts